import { logger } from "@/utils/logger";
import React from 'react';
import { RefreshCw, X, Copy, Sparkles, MoreVertical, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { useLanguage } from '@/contexts/LanguageContext';
import { getDealerLogoURL, getDealerID } from '@/utils/dealerDataUtils';
// import { useOpenAIConnectionHealthMonitor } from '@/hooks/useOpenAIConnectionHealthMonitor'; // Disabled - network issues
import { DealerManager } from '@/utils/dealerManager';
import { sessionManager } from '@/utils/sessionManager';
import LanguageToggle from '../LanguageToggle';
import { securedSupabase } from '@/integrations/supabase/secured-client';
import { useIsMobile } from '@/hooks/use-mobile';

import { useCertificationStyles } from '@/hooks/useCertificationStyles';

interface ChatHeaderProps {
  onNewConversation: () => void;
  onCopyConversation: (messagesContainerRef?: React.RefObject<HTMLElement>) => void;
  onCollapse: () => void;
  onSendMessage?: (message: string) => void;
  messagesContainerRef?: React.RefObject<HTMLElement>;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onNewConversation,
  onCopyConversation,
  onCollapse,
  onSendMessage,
  messagesContainerRef,
}) => {
  const { t, language } = useLanguage();
  const [dealerLogoURL, setDealerLogoURL] = React.useState<string | null>(null);
  const [dealerId, setDealerId] = React.useState<number | null>(null);
  const logoURL = dealerLogoURL;
  const { styles } = useCertificationStyles();
  const isMobile = useIsMobile();
  
  const [isConnected, setIsConnected] = React.useState(true); // OpenAI API is working, dealer auth is broken
  const [completedTests, setCompletedTests] = React.useState<Set<number>>(new Set());
  const [popularQuestionCategories, setPopularQuestionCategories] = React.useState<Array<{name: string, prompts: string[]}>>([]);
  const [refreshTrigger, setRefreshTrigger] = React.useState<number>(0);

  // Check if logo click is enabled for this dealer
  const isLogoClickEnabled = dealerId === 2 || dealerId === 3;

  // Completely disable health monitoring - OpenAI API has intermittent network issues in Lovable environment
  // const { forceHealthCheck } = useOpenAIConnectionHealthMonitor({
  //   enabled: false,
  //   checkIntervalMs: 5 * 60 * 1000,
  //   onConnectionRestored: () => {
  

  
  

  const getConnectionTooltip = () => {
    return isConnected 
      ? t('chat.agent.connected')
      : t('chat.agent.disconnected');
  };

  const getSparklesColor = () => {
    return isConnected ? "!text-green-400" : "!text-red-400";
  };

  // Hardcoded category order to maintain consistency
  const categoryOrder = [
    'General Info',
    'Inventory Search',
    'New Vehicles', 
    'Used Vehicles',
    'Service & Parts',
    'Financing',
    'Trade Estimates',
    'Promotions',
    'Service',
    'Appointments',
    'Showroom'
  ];

  // Fetch dealer logo and dealer ID (with retry for DOM fallback)
  React.useEffect(() => {
    let cancelled = false;
    const fetchDealerData = async (attempt = 0) => {
      const [logoURL, id] = await Promise.all([
        getDealerLogoURL(),
        getDealerID()
      ]);
      if (cancelled) return;
      setDealerId(id);
      if (logoURL) {
        setDealerLogoURL(logoURL);
      } else if (attempt < 3) {
        // Retry after delay - DOM logo element may not be rendered yet
        setTimeout(() => fetchDealerData(attempt + 1), 1000 * (attempt + 1));
      }
    };
    fetchDealerData();
    return () => { cancelled = true; };
  }, []);


  React.useEffect(() => {
    const fetchPopularQuestions = async () => {
      try {
        // Check if popular questions are enabled for this dealer
        const { DealerManager } = await import('@/utils/dealerManager');
        const isEnabled = DealerManager.isPopularQuestionsEnabled();
        
        if (!isEnabled) {
          
          setPopularQuestionCategories([]);
          return;
        }
        
        let dealerId = await getDealerID();
        
        
        
        // Fallback for development/preview environments
        if (!dealerId && window.location.hostname.includes('lovableproject.com')) {
          
          dealerId = 3; // Use dealer 3 as fallback for testing
        }
        
        if (!dealerId) {
          
          return;
        }

        // Use the SECURITY DEFINER RPC to bypass the header-based RLS policy
        // that Supabase's gateway strips for anonymous requests (same fix pattern
        // as get_prompt_menu).
        let { data, error } = await securedSupabase.raw
          .rpc('get_popular_questions', { p_dealer_id: dealerId });

        if (error) {
          
          return;
        }


        

        // Group prompts by category and sort alphabetically within each
        const groupedPrompts: Record<string, string[]> = {};
        
        data?.forEach(prompt => {
          const categoryName = language === 'fr' ? prompt.category_fr : prompt.category_en;
          const promptText = language === 'fr' ? prompt.prompt_fr : prompt.prompt_en;
          
          if (!groupedPrompts[categoryName]) {
            groupedPrompts[categoryName] = [];
          }
          groupedPrompts[categoryName].push(promptText);
        });

        // Sort prompts alphabetically within each category
        Object.keys(groupedPrompts).forEach(category => {
          groupedPrompts[category].sort();
        });

        // Create categories array directly from the grouped data, maintaining database order
        const categories = Object.keys(groupedPrompts)
          .map(categoryName => ({
            name: categoryName,
            prompts: groupedPrompts[categoryName]
          }))
          .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by category name

        setPopularQuestionCategories(categories);
      } catch (error) {
        
      }
    };

    fetchPopularQuestions();
  }, [language, refreshTrigger]);

  // Helper function to get French category translation
  const getCategoryTranslation = (categoryEn: string): string => {
    const translations: Record<string, string> = {
      'General Info': 'Informations générales',
      'Inventory Search': 'Recherche d\'inventaire',
      'New Vehicles': 'Véhicules neufs',
      'Used Vehicles': 'Véhicules d\'occasion',
      'Service & Parts': 'Service et pièces',
      'Financing': 'Financement',
      'Trade Estimates': 'Valeur d\'échange',
      'Promotions': 'Promotions',
      'Service': 'Service',
      'Appointments': 'Rendez-vous',
      'Showroom': 'Salle d\'exposition'
    };
    return translations[categoryEn] || categoryEn;
  };

  // Flatten all prompts for progress calculation
  const allPrompts = popularQuestionCategories.flatMap(category => category.prompts);

  const handlePopularQuestion = (prompt: string) => {
    if (onSendMessage) {
      onSendMessage(prompt);
    }
  };

  const handleNewConversation = () => {
    setCompletedTests(new Set());
    // Clear dealer cache to force re-authentication and refresh all keys/assistant IDs
    DealerManager.clearCache();
    // Clear session and create a new one with a new session ID
    sessionManager.clearSession();
    sessionManager.initializeSession();
    // Trigger fresh fetch of popular questions
    setRefreshTrigger(prev => prev + 1);
    
    onNewConversation();
  };

  const toggleTestCompletion = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const newCompletedTests = new Set(completedTests);
    if (newCompletedTests.has(index)) {
      newCompletedTests.delete(index);
    } else {
      newCompletedTests.add(index);
    }
    setCompletedTests(newCompletedTests);
  };

  const progressPercentage = (completedTests.size / allPrompts.length) * 100;

  const handleLogoClick = async () => {
    const dealerId = await getDealerID();

    // Only allow click for dealer IDs 2 and 3
    if (dealerId !== 2 && dealerId !== 3) {
      return;
    }

    // Import and trigger the debug modal
    const { debugLogger } = await import('@/utils/debugLogger');
    debugLogger.triggerDebugModal();
  };

  return (
    <div 
      className="p-4 flex items-center justify-between relative overflow-hidden flex-shrink-0"
      style={{ 
        backgroundColor: styles.header_bg_col,
        color: styles.header_text_col,
        borderTopLeftRadius: `${styles.corner_radius}px`,
        borderTopRightRadius: `${styles.corner_radius}px`
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-black/20 animate-pulse" />
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-white/10 rounded-full animate-bounce" />
      <div className="absolute -bottom-1 -left-1 w-6 h-6 bg-white/10 rounded-full animate-pulse delay-300" />
      
      <div className="flex items-center space-x-3 relative">
        <div 
          className="w-10 h-10 flex items-center justify-center transition-colors rounded-full cursor-pointer overflow-hidden"
          style={{ backgroundColor: logoURL ? 'var(--admin-general-header-bg-color, #1f1f1f)' : 'rgba(255,255,255,0.15)' }}
          onClick={isLogoClickEnabled ? handleLogoClick : undefined}
        >
          {logoURL ? (
            <img 
              src={logoURL}
              alt="Dealer Logo" 
              className="w-8 h-8 object-contain"
              onError={(e) => {
                logger.warn('Logo failed to load:', logoURL);
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <Sparkles className="w-5 h-5" style={{ color: styles.header_text_col }} />
          )}
        </div>
        <div className="flex items-center">
          <div className="font-semibold text-lg flex items-center space-x-2" style={{ color: styles.header_text_col }}>
            <span>{t('chat.agent.name')}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span style={{ color: isConnected ? '#4ade80' : '#f87171' }}>
                  <Sparkles 
                    className="w-4 h-4 animate-pulse" 
                    style={{ 
                      color: 'inherit',
                      fill: 'currentColor'
                    }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">{getConnectionTooltip()}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 relative">
        <LanguageToggle />
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-white/20 p-1 transition-all duration-200 hover:scale-110"
          style={{ color: styles.header_text_col }}
          onClick={() => onCopyConversation(messagesContainerRef)}
          title={t('chat.copy.conversation')}
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-white/20 p-1 transition-all duration-200 hover:scale-110"
          style={{ color: styles.header_text_col }}
          onClick={handleNewConversation}
          title={t('chat.new.conversation')}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
        {popularQuestionCategories.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="hover:bg-white/20 p-1 transition-all duration-200 hover:scale-110 d2cbot-bot_border-0"
                style={{ color: styles.header_text_col }}
                title="Popular Questions"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
          <DropdownMenuContent className="z-[999999] bg-white d2cbot-bot_border d2cbot-bot_border-gray-200 shadow-xl w-80 rounded-xl p-0 animate-in fade-in-0 zoom-in-95 duration-200 max-h-[70vh] overflow-hidden" align="end">
            <div className="p-3 pb-0">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('chat.popular.questions.title')}</div>
              
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{completedTests.size}/{allPrompts.length}</span>
                </div>
                <Progress value={progressPercentage} className="h-2" />
              </div>
            </div>
            
            {/* Scrollable content area */}
            <div className="overflow-y-auto max-h-[calc(70vh-120px)] px-3 pb-3">
              {popularQuestionCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-4 last:mb-0">
                  {/* Category Header */}
                  <div className="py-1 mb-2">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {category.name}
                    </h4>
                  </div>
                  
                  {/* Category Prompts */}
                  {category.prompts.length > 0 ? (
                    category.prompts.map((prompt, promptIndex) => {
                      const globalIndex = allPrompts.indexOf(prompt);
                      const isCompleted = completedTests.has(globalIndex);
                      return (
                        <DropdownMenuItem
                          key={`${categoryIndex}-${promptIndex}`}
                          className={`text-sm cursor-pointer px-4 py-3 text-left rounded-lg transition-all duration-200 hover:shadow-sm d2cbot-bot_border-0 mb-1 last:mb-0 ${
                            isCompleted 
                              ? 'bg-gray-100 text-gray-400 hover:bg-gray-150' 
                              : 'hover:bg-gray-50 font-medium text-gray-700 hover:text-gray-900'
                          }`}
                          onClick={() => handlePopularQuestion(prompt)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isCompleted}
                              onCheckedChange={() => toggleTestCompletion(globalIndex, event as any)}
                              onClick={(e) => toggleTestCompletion(globalIndex, e)}
                              className="flex-shrink-0"
                            />
                            <span className="leading-relaxed">{globalIndex + 1}. {prompt}</span>
                          </div>
                        </DropdownMenuItem>
                      );
                    })
                  ) : (
                    <div className="px-4 py-2 text-xs text-gray-400 italic">
                      {language === 'fr' ? 'Aucun prompt disponible' : 'No prompts available'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={`chatbot-close-btn d2cbot-btn-fixed flex-shrink-0 transition-all duration-200 !aspect-square ${
            isMobile 
              ? 'bg-white/20 hover:bg-white/30 rounded-lg border-2 border-white/30 shadow-lg backdrop-blur-sm hover:scale-105' 
              : 'bg-white/20 hover:bg-white/30 rounded-lg border-2 border-white/40 shadow-md hover:scale-105'
          }`}
          style={{ 
            color: '#ffffff',
            width: isMobile ? '48px' : '40px',
            height: isMobile ? '48px' : '40px',
            minWidth: isMobile ? '48px' : '40px',
            maxWidth: isMobile ? '48px' : '40px',
            aspectRatio: '1 / 1',
          }}
          onClick={onCollapse}
          title="Collapse chat"
        >
          <ChevronDown className={isMobile ? "w-7 h-7 font-bold" : "w-5 h-5"} strokeWidth={3} />
        </Button>
      </div>
    </div>
  );
};

export default ChatHeader;